import React, { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import {
  Settings,
  Users,
  CreditCard,
  Gift,
  Crown,
  MessageSquare,
  LogOut,
  Zap,
  Library,
  Loader2,
  Share2,
  Calendar,
  Layers,
  Target,
} from 'lucide-react';
import { sendRequest } from "../api";
import { openFeedback } from '../utils';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUser } from '../providers/UserProvider';
import { useBottomBar } from '../providers/BottomBarProvider';
import { ReferralModal } from './ReferralModal';
import './Navbar.css';

// Custom Oplae Icon component
const OplaeIcon = ({ className }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 21.5C16.6944 21.5 20.5 17.6944 20.5 13C20.5 8.5 12 1.5 12 1.5C12 1.5 3.5 8.5 3.5 13C3.5 17.6944 7.30558 21.5 12 21.5Z"
    />
  </svg>
);

const LeftSidebar = ({ onError, theme = 'light' }) => {
  const { showBottomBar, bottomBarError } = useBottomBar();
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [showReferralModal, setShowReferralModal] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, fetchUser } = useUser();

  const handleBilling = async () => {
    navigate('/upgrade');
  };

  const handleReferralClick = () => {
    setShowReferralModal(true);
  };

  const handleFeedback = () => {
    openFeedback();
  };

  const handleLogout = async () => {
    window.location.href = `${process.env.API_BASE_URL}/logout`;
  };

  const accountItems = [
    {
      icon: <CreditCard className="h-5 w-5" />,
      label: "Billing",
      onClick: handleBilling,
      className: theme === 'light' ? "text-gray-600 hover:text-gray-900" : "text-gray-400 hover:text-gray-200",
    },
    {
      icon: <Gift className="h-5 w-5" />,
      label: "Share & Earn",
      onClick: handleReferralClick,
      className: theme === 'light' ? "text-gray-600 hover:text-gray-900" : "text-gray-400 hover:text-gray-200"
    },
    {
      icon: <MessageSquare className="h-5 w-5" />,
      label: "Send Feedback",
      onClick: handleFeedback,
      className: theme === 'light' ? "text-gray-600 hover:text-gray-900" : "text-gray-400 hover:text-gray-200"
    },
    {
      icon: <LogOut className="h-5 w-5" />,
      label: "Log Out",
      onClick: handleLogout,
      className: "text-red-600 hover:text-red-700"
    }
  ];

  return (
    <div className={cn(
      "fixed left-0 top-[var(--navbar-height)]",
      "h-[calc(100vh-var(--navbar-height))]",
      "w-20 backdrop-blur-[32px] border-r",
      "flex flex-col items-center py-4",
      "transition-all duration-300",
      theme === 'light'
        ? "bg-white/60 border-gray-200/50"
        : "bg-gray-800/60 border-gray-700/50"
    )}>
      {/* Main Navigation Buttons */}
      <div className="flex flex-col items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          className={cn(
            "transition-all duration-300 hover:scale-105 relative group",
            theme === 'light'
              ? "hover:bg-gray-50/50"
              : "hover:bg-gray-700/50",
            location.pathname === '/library' && (theme === 'light' 
              ? "bg-gray-100 text-gray-900"
              : "bg-gray-700 text-gray-100")
          )}
          onClick={() => navigate('/library')}
        >
          <Library className={cn(
            "h-5 w-5",
            theme === 'light' ? "text-gray-600" : "text-gray-400"
          )} />
          <span className={cn(
            "absolute left-full ml-2 px-2 py-1 text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none",
            theme === 'light'
              ? "bg-gray-900 text-white"
              : "bg-gray-700 text-gray-100"
          )}>
            Library
          </span>
        </Button>

        <Button
          variant="ghost"
          size="icon"
          className={cn(
            "transition-all duration-300 hover:scale-105 relative group",
            theme === 'light'
              ? "hover:bg-gray-50/50"
              : "hover:bg-gray-700/50",
            location.pathname === '/stats' && (theme === 'light' 
              ? "bg-gray-100 text-gray-900"
              : "bg-gray-700 text-gray-100")
          )}
          onClick={() => navigate('/stats')}
        >
          <Layers className={cn(
            "h-5 w-5",
            theme === 'light' ? "text-gray-600" : "text-gray-400"
          )} />
          <span className={cn(
            "absolute left-full ml-2 px-2 py-1 text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none",
            theme === 'light'
              ? "bg-gray-900 text-white"
              : "bg-gray-700 text-gray-100"
          )}>
            Stats
          </span>
        </Button>

        <Button
          variant="ghost"
          size="icon"
          className={cn(
            "transition-all duration-300 hover:scale-105 relative group",
            theme === 'light'
              ? "hover:bg-gray-50/50"
              : "hover:bg-gray-700/50",
            location.pathname === '/settings' && (theme === 'light' 
              ? "bg-gray-100 text-gray-900"
              : "bg-gray-700 text-gray-100")
          )}
          onClick={() => navigate('/settings')}
        >
          <Users className={cn(
            "h-5 w-5",
            theme === 'light' ? "text-gray-600" : "text-gray-400"
          )} />
          <span className={cn(
            "absolute left-full ml-2 px-2 py-1 text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none",
            theme === 'light'
              ? "bg-gray-900 text-white"
              : "bg-gray-700 text-gray-100"
          )}>
            Connected Accounts
          </span>
        </Button>
      </div>

      {/* Settings Section with Animated Menu */}
      <div className="relative mt-auto">
        <Button 
          variant="ghost" 
          size="icon"
          className={cn(
            "transition-all duration-300 hover:scale-105 relative group",
            theme === 'light'
              ? "hover:bg-gray-50/50"
              : "hover:bg-gray-700/50",
            isSettingsOpen && (theme === 'light' 
              ? "bg-gray-100 text-gray-900"
              : "bg-gray-700 text-gray-100")
          )}
          onClick={() => setIsSettingsOpen(!isSettingsOpen)}
        >
          <Settings className={cn(
            "h-5 w-5 transition-transform duration-300",
            isSettingsOpen && "rotate-180",
            theme === 'light' ? "text-gray-600" : "text-gray-400"
          )} />
          <span className={cn(
            "absolute left-full ml-2 px-2 py-1 text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none",
            theme === 'light'
              ? "bg-gray-900 text-white"
              : "bg-gray-700 text-gray-100"
          )}>
            Settings
          </span>
        </Button>

        {/* Animated Settings Menu */}
        <div className={cn(
          "absolute bottom-full mb-2 left-1/2 -translate-x-1/2",
          "flex flex-col gap-1 items-center",
          "transition-all duration-300 origin-bottom",
          isSettingsOpen ? "opacity-100 scale-100" : "opacity-0 scale-0 pointer-events-none"
        )}>
          {accountItems.map((item, index) => (
            <Button
              key={index}
              variant="ghost"
              size="icon"
              className={cn(
                "transition-all duration-300 relative group",
                item.className,
                "transform",
                isSettingsOpen ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
              )}
              style={{ 
                transitionDelay: isSettingsOpen ? `${index * 50}ms` : '0ms'
              }}
              onClick={item.onClick}
            >
              {item.icon}
              <span className={cn(
                "absolute left-full ml-2 px-2 py-1 text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none",
                theme === 'light'
                  ? "bg-gray-900 text-white"
                  : "bg-gray-700 text-gray-100"
              )}>
                {item.label}
              </span>
            </Button>
          ))}
        </div>
      </div>

      <ReferralModal
        open={showReferralModal}
        onOpenChange={setShowReferralModal}
        referralCode={user?.referral_code}
        theme={theme}
      />
    </div>
  );
};

export default LeftSidebar; 